import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./dashboard.css";
import moment from "moment";
import MGAAPI from "../../api/index";
import MGACreateProjectDialog from "../../components/mgaProjectDailog/index";
import DataTable from "../../components/dataTable/index";
import PieChart from "../../components/dashboarCharts/index";
import { useTour } from "../../utils/tour";
import "../../utils/tour/tour.css";
import { tourAtom } from "../../utils/atoms/tourAtom";
import { useAtom } from "jotai";
import { componentsLoaderAtom } from "../../utils/atoms/componentsLoader";
import { Card, Text, Grid, Radio, useMantineColorScheme } from "@mantine/core";
import {
  IconCalendar,
  IconCertificate,
  IconShieldCheckFilled,
} from "@tabler/icons-react";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import { useTranslation } from "react-i18next";

const MGAAPIS = new MGAAPI();
const Dashboard = () => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const [userScope] = useAtom(userScopeAtom);
  
  const isLightMode = colorScheme === "light";
  const [tour] = useAtom(tourAtom);
  const { startTour } = useTour();
  const [dashboardState, setDashboardState] = useState({
    createProjectPopupStatus: false,
  });
  const [, setLoading] = useState(true);
  const [mgaProjects, setMGAProjectState] = useState([]);
  const [highlightedProjectId, setHighlightedProjectId] = useState(null);
  const [, setComponentsLoader] = useAtom(componentsLoaderAtom);
  const [ConfigTypeForUser, setConfigTypeForUser] = useState([]);
  const highlightTimeoutRef = useRef(null);
  const createProjectHandler = () =>
    setDashboardState({ createProjectPopupStatus: true });
  const closeCreateProjectModalHandler = () =>
    setDashboardState({ createProjectPopupStatus: false });
  useEffect(() => {
    if (tour) {
      startTour();
    }
  }, [tour, startTour]);

  const refreshProjects = useCallback(
    (highlightProjectIdFromEvent = null) => {
      setComponentsLoader(true);
      MGAAPIS.getProjects()
        .then((res) => {
          const sortedRes = [...res].sort(
            (a, b) => new Date(b.updatedOn) - new Date(a.updatedOn)
          );
          setMGAProjectState(sortedRes);

          if (highlightProjectIdFromEvent) {
            const exists = sortedRes.some(
              (project) =>
                String(project?.projectId) ===
                String(highlightProjectIdFromEvent)
            );
            if (exists) {
              setHighlightedProjectId(highlightProjectIdFromEvent);
              if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
              }
              highlightTimeoutRef.current = setTimeout(() => {
                setHighlightedProjectId(null);
                highlightTimeoutRef.current = null;
              }, 5000);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching projects:", error);
        })
        .finally(() => {
          setLoading(false);
          setComponentsLoader(false);
        });
    },
    [setComponentsLoader]
  );

  useEffect(() => {
    const handleProjectsRefresh = (evt) =>
      refreshProjects(evt?.detail?.highlightProjectId);
    window.addEventListener("mga-projects-refresh", handleProjectsRefresh);
    return () => window.removeEventListener("mga-projects-refresh", handleProjectsRefresh);
  }, [refreshProjects]);

  useEffect(() => {
    refreshProjects();
    MGAAPIS.getmgConfigTypeForUser().then((res) => setConfigTypeForUser(res));
  }, [refreshProjects]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const formattedSubscriptionEndDate = useMemo(() => {
    const raw = userScope?.subscriptionEndDate;
    if (!raw) return null;
    const m = moment.utc(raw);
    if (!m.isValid()) return null;
    return m.format('YYYY - MMM - DD'); // Example: 2026 - Jul - 09
  }, [userScope?.subscriptionEndDate]);

  return (
    <div
      className="container-fluid"
      style={{ overflow: "hidden", overflowY: "auto", height: "100%" }}
    >
      <>
        <div className="row mt-2 mb-2">
          <div className="col-sm-1"></div>
        </div>
        <div className="row equal-height" style={{ alignContent: "center" }}>
          <div className="col-sm-1"></div>
          <div className="col-sm-11">
            <div className="row">
              <div className="col-lg-9 col-md-12 col-sm-12 mb-3">
                <div>
                  <DataTable
                    data={mgaProjects}
                    className="datatable"
                    createProjectHandler={createProjectHandler}
                    refreshProjects={refreshProjects}
                    highlightProjectId={highlightedProjectId}
                  />
                </div>
              </div>
              { (
                <div className="col-lg-3 col-md-12 col-sm-12 mb-3 d-flex flex-column mt-5">
                  {mgaProjects.length > 0 && <PieChart
                    chartData1={mgaProjects}
                    chartData2={ConfigTypeForUser}
                  />}
                  {/* License Details Box using Mantine v6 */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 40,
                      width: "100%",
                    }}
                  >
                    <Card
                      data-intro={t("dashboard.licenseDetails")}
                      data-position="left"
                      shadow="sm"
                      radius="md"
                      withBorder
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        minWidth: 0,
                        background: isLightMode
                          ? "#fff"
                          : "var(--se-background-alternative)",
                        boxSizing: "border-box",
                      }}
                    >
                      <Grid
                        gutter="xs"
                        mb="xs"
                        align="center"
                        style={{
                          background: isLightMode ? "#E6E6E6" : "#3a3c3e",
                          borderTopLeftRadius: 8,
                          borderTopRightRadius: 8,
                          marginLeft: -16,
                          marginRight: -16,
                          marginTop: -16,
                          padding: "12px 16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Grid.Col
                          span={2}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            paddingTop: 0,
                            paddingBottom: 0,
                            flexDirection: "column",
                          }}
                        >
                          {/* Use a wrapper div with full height and flex to center vertically */}
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginLeft: 100,
                            }}
                          >
                            <IconCertificate
                              size={28}
                              style={{
                                color: isLightMode ? "#9fa0a4" : "#d5d7e0",
                                // The following ensures vertical and horizontal centering
                                margin: "0 auto",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                              }}
                            />
                          </div>
                        </Grid.Col>
                        <Grid.Col
                          span={10}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          <Text
                            fw={600}
                            size="lg"
                            style={{
                              marginBottom: 0,
                              color: isLightMode ? "#4f555c" : "#fff",
                              textAlign: "center",
                              width: "100%",
                            }}
                          >
                            {t("dashboard.license.header")}
                          </Text>
                        </Grid.Col>
                      </Grid>

                      <Grid gutter="xs" mb="xs" align="center">
                        <Grid.Col
                          span={2}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <IconShieldCheckFilled
                            size={28}
                            style={{
                              color: isLightMode ? "#9fa0a4" : "#d5d7e0",
                            }}
                          />
                        </Grid.Col>
                        <Grid.Col span={10}>
                          <Text
                            fw={500}
                            color={isLightMode ? "#9fa0a4" : "#fff"}
                          >
                            {t("dashboard.license.type")}
                          </Text>
                          <Text fw={600} color={isLightMode && "#4f555c"}>
                            {t("dashboard.license.singleUser")}
                          </Text>
                        </Grid.Col>
                      </Grid>
                      <div
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          margin: "0 0 12px 0",
                        }}
                      />
                      <Grid gutter="xs" mb="xs" align="center">
                        <Grid.Col
                          span={2}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <IconCalendar
                            size={28}
                            style={{
                              color: isLightMode ? "#9fa0a4" : "#d5d7e0",
                            }}
                          />
                        </Grid.Col>
                        <Grid.Col span={7}>
                          <Text
                            fw={500}
                            color={isLightMode ? "#9fa0a4" : "#fff"}
                          >
                            {t("dashboard.license.validTill")}
                          </Text>
                          <Text fw={600} color={isLightMode && "#4f555c"}>
                            {formattedSubscriptionEndDate || t("common.notAvailable")}
                          </Text>
                        </Grid.Col>
                        <Grid.Col
                          span={3}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                          }}
                        >
                          {/* <button
                            style={{
                              padding: "4px 12px",
                              background: "#228be6",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "0.9em",
                              cursor: "pointer",
                            }}
                          >
                            Extend
                          </button> */}
                        </Grid.Col>
                      </Grid>
                      <div
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          margin: "0 0 12px 0",
                        }}
                      />
                      <Grid gutter="xs" align="center">
                        <Grid.Col
                          span={2}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Radio
                            checked
                            readOnly
                            value="active"
                            color="blue"
                            size="md"
                            style={{ pointerEvents: "none" }}
                          />
                        </Grid.Col>
                        <Grid.Col span={10}>
                          <Text
                            fw={500}
                            color={isLightMode ? "#9fa0a4" : "#fff"}
                          >
                            {t("dashboard.license.status")}
                          </Text>
                          <Text fw={600} color={isLightMode && "#4f555c"}>
                            {t("dashboard.license.active")}
                          </Text>
                        </Grid.Col>
                      </Grid>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>

      {dashboardState?.createProjectPopupStatus && (
        <MGACreateProjectDialog
          createProjectPopupStatus={dashboardState.createProjectPopupStatus}
          closeCreateProjectModalHandler={closeCreateProjectModalHandler}
          state={setMGAProjectState}
        />
      )}
        <se-snackbar
        id="snackbar"
        type="success"
        icon="information_stroke"
        message={t("dashboard.snackbar.projectDataSuccess")}
        canClose={true}
      ></se-snackbar>
    </div>
  );
};

export default Dashboard;